import React, { useState, useMemo } from "react";
import { 
  Container, 
  Grid, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Box,
  TextField,
  Slider,
  Chip,
  Button,
  Pagination,
  InputAdornment,
  Drawer,
  IconButton,
  useMediaQuery,
  Paper,
  alpha,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardMedia,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tab,
  Tabs,
  CircularProgress
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
// Import icons from @mui/icons-material
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import PublicIcon from '@mui/icons-material/Public';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import VerifiedIcon from '@mui/icons-material/Verified';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import { motion } from 'framer-motion';

// Import coins from your data files
import { australianCoins } from "../components/FeaturedCoins";
import { canadianCoins } from "../pages/CanadaPage";
import { usaCoins } from "../pages/USAPage";
import { ukCoins } from "../pages/UKPage";
import CoinCard from "../components/CoinCard";

// Combine all coins from every country
const allCoins = [...(australianCoins || []), ...(canadianCoins || []), ...(usaCoins || []), ...(ukCoins || [])];

// Process coins to ensure they have all required fields
const processedCoins = allCoins.map(coin => ({
  ...coin,
  country: coin.country || 
    (coin.category === 'australia' ? 'Australia' : 
     coin.category === 'canada' ? 'Canada' : 
     coin.category === 'usa' ? 'USA' : 
     coin.category === 'uk' ? 'UK' : 'Various'),
  era: coin.era || 
    (coin.year ? 
      (parseInt(coin.year) < 1800 ? 'Ancient' :
       parseInt(coin.year) < 1900 ? '19th Century' :
       parseInt(coin.year) < 1950 ? 'Early 20th Century' :
       parseInt(coin.year) < 2000 ? 'Late 20th Century' : 'Modern') : 'Various'),
  metal: coin.metal || 'Various',
  grade: coin.grade || 'Not Graded'
}));

// Special Silver Eagles Collection - Only American Silver Eagle
const silverEagles = [
  {
    id: 'silver-001',
    name: "American Silver Eagle - BU",
    year: "2024",
    grade: "BU (Brilliant Uncirculated)",
    gradeNum: 70,
    price: "$99.00",
    priceNum: 99,
    priceWithFee: 101.48,
    image: "/assets/images/usacoins/1 oz American Silver Eagle Coin1 oz American Silver Eagle Coin.png",
    description: "Newly minted American Silver Eagle. 1 oz .999 fine silver. Official US Mint issue with the iconic Walking Liberty design. Free Shipping included.",
    metal: "Silver",
    category: "usa",
    mint: "US Mint",
    country: "USA",
    specialOffer: true,
    paypalEligible: true,
    type: "Silver Eagle",
    freeShipping: true
  }
];

// Canadian Gold Coins - Only Gold MapleLeaf
const goldCoins = [
  {
    id: 'gold-001',
    name: "Canadian Gold MapleLeaf - 1 oz",
    year: "2024",
    grade: "BU (Brilliant Uncirculated)",
    gradeNum: 70,
    price: "Contact for Pricing",
    priceNum: 0,
    image: "/assets/images/usacoins/Gold Maple Leaf .999.png",
    description: "2024 Canadian Gold MapleLeaf. 1 oz .9999 fine gold. Face Value: $50.00 CAD. Official Royal Canadian Mint issue with iconic maple leaf design. Contact us for current spot price based retail pricing.",
    metal: "Gold",
    category: "canada",
    mint: "Royal Canadian Mint",
    country: "Canada",
    specialOffer: false,
    paypalEligible: false,
    type: "Gold MapleLeaf",
    faceValue: "$50.00 CAD"
  }
];

// Silver/Gold Eagles History
const eagleHistory = [
  {
    title: "American Silver Eagle",
    year: "1986 - Present",
    description: "The American Silver Eagle is the official silver bullion coin of the United States. It was first released by the U.S. Mint on November 24, 1986. The coin is struck only in the one-troy ounce size, which has a nominal face value of one dollar and is guaranteed to contain one troy ounce of 99.9% pure silver. The obverse design is Adolph A. Weinman's Walking Liberty, which was originally used on the Walking Liberty half dollar from 1916 to 1947. The reverse features a heraldic eagle designed by John Mercanti.",
    image: "/assets/images/usacoins/1 oz American Silver Eagle Coin1 oz American Silver Eagle Coin.png",
    specs: {
      weight: "31.103g (1 oz)",
      purity: "99.9% Silver",
      diameter: "40.6mm",
      thickness: "2.98mm",
      mintage: "Varies by year"
    }
  },
  {
    title: "Canadian Gold MapleLeaf",
    year: "1979 - Present",
    description: "The Canadian Gold MapleLeaf is the official gold bullion coin of Canada. First introduced by the Royal Canadian Mint in 1979, it is struck in .9999 fine gold (99.99% pure), making it one of the purest gold bullion coins in the world. The coin is available in 1 oz, 1/2 oz, 1/4 oz, 1/10 oz, and 1/20 oz sizes. The design features the iconic maple leaf on the reverse and the effigy of Queen Elizabeth II on the obverse.",
    image: "/assets/images/usacoins/Gold Maple Leaf .999.png",
    specs: {
      weight: "31.103g (1 oz)",
      purity: "99.99% Gold",
      diameter: "30mm",
      thickness: "2.8mm",
      mintage: "Varies by year"
    }
  }
];

const BuyCoins = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Set default tab to Special Offer (index 0)
  const [tabValue, setTabValue] = useState(0);
  const [filters, setFilters] = useState({
    searchQuery: '',
    era: '',
    metal: '',
    country: '',
    priceRange: [0, 100000],
    grade: '',
    year: ''
  });
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [sortBy, setSortBy] = useState('newest');
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    country: '',
    postalCode: '',
    message: '',
    agreeToTerms: false,
    quantity: 1,
    cardNumber: '',
    cardExpiry: '',
    cardCVC: '',
    cardName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    billingCountry: ''
  });

  const itemsPerPage = 9;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Combine all special offer coins
  const allSpecialCoins = [...silverEagles, ...goldCoins];

  // Extract unique values from coins data for filters
  const eras = useMemo(() => {
    const uniqueEras = [...new Set(processedCoins.map(coin => coin.era).filter(Boolean))];
    return ['All', ...uniqueEras.sort()];
  }, []);

  const metals = useMemo(() => {
    const uniqueMetals = [...new Set(processedCoins.map(coin => coin.metal).filter(Boolean))];
    return ['All', ...uniqueMetals.sort()];
  }, []);

  const countriesList = useMemo(() => {
    const uniqueCountries = [...new Set(processedCoins.map(coin => coin.country).filter(Boolean))];
    return uniqueCountries.sort();
  }, []);

  const grades = useMemo(() => {
    const uniqueGrades = [...new Set(processedCoins.map(coin => coin.grade).filter(Boolean))];
    const sorted = uniqueGrades.sort((a, b) => {
      if (a.includes('Proof') && !b.includes('Proof')) return -1;
      if (!a.includes('Proof') && b.includes('Proof')) return 1;
      return a.localeCompare(b);
    });
    return ['All', ...sorted];
  }, []);

  // Filter coins based on all filters
  const filteredCoins = useMemo(() => {
    let result = [...processedCoins];

    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(coin => 
        coin.name?.toLowerCase().includes(query) ||
        coin.description?.toLowerCase().includes(query) ||
        coin.year?.toString().includes(query)
      );
    }

    if (filters.era && filters.era !== 'All') {
      result = result.filter(coin => coin.era === filters.era);
    }

    if (filters.metal && filters.metal !== 'All') {
      result = result.filter(coin => coin.metal === filters.metal);
    }

    if (filters.country && filters.country !== '') {
      result = result.filter(coin => coin.country === filters.country);
    }

    if (filters.grade && filters.grade !== 'All') {
      result = result.filter(coin => coin.grade === filters.grade);
    }

    if (filters.year && filters.year !== '') {
      result = result.filter(coin => coin.year?.toString().includes(filters.year));
    }

    result = result.filter(coin => {
      let coinPrice = coin.priceNum;
      if (!coinPrice && coin.price) {
        const priceMatch = coin.price.match(/\d[\d,]*/);
        if (priceMatch) {
          coinPrice = parseInt(priceMatch[0].replace(/,/g, ''));
        }
      }
      return coinPrice >= filters.priceRange[0] && coinPrice <= filters.priceRange[1];
    });

    switch (sortBy) {
      case 'price-asc':
        result.sort((a, b) => (a.priceNum || 0) - (b.priceNum || 0));
        break;
      case 'price-desc':
        result.sort((a, b) => (b.priceNum || 0) - (a.priceNum || 0));
        break;
      case 'grade':
        result.sort((a, b) => {
          const getGradeValue = (grade) => {
            if (grade?.includes('Proof')) return 100;
            const numMatch = grade?.match(/\d+/);
            return numMatch ? parseInt(numMatch[0]) : 0;
          };
          return getGradeValue(b.grade) - getGradeValue(a.grade);
        });
        break;
      case 'newest':
      default:
        result.sort((a, b) => {
          const yearA = parseInt(a.year) || 0;
          const yearB = parseInt(b.year) || 0;
          return yearB - yearA;
        });
        break;
    }

    return result;
  }, [processedCoins, filters, sortBy]);

  const totalPages = Math.ceil(filteredCoins.length / itemsPerPage);
  const paginatedCoins = filteredCoins.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      searchQuery: '',
      era: '',
      metal: '',
      country: '',
      priceRange: [0, 100000],
      grade: '',
      year: ''
    });
    setPage(1);
  };

  const handleBuyClick = (coin) => {
    setSelectedCoin(coin);
    setBuyDialogOpen(true);
    setActiveStep(0);
  };

  const handlePayPalPurchase = (coin) => {
    if (!coin.paypalEligible) {
      setSnackbarMessage('This coin requires email inquiry. Please use the "Email Inquiry" option.');
      setSnackbarSeverity('info');
      setSnackbarOpen(true);
      return;
    }
    
    setSelectedCoin(coin);
    setActiveStep(0);
    setBuyDialogOpen(true);
  };

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateTotalWithFee = (basePrice, quantity = 1) => {
    const fee = basePrice * 0.025;
    const total = (basePrice + fee) * quantity;
    return {
      basePrice: basePrice,
      fee: fee,
      total: total,
      formattedTotal: `$${total.toFixed(2)}`
    };
  };

  // Send email using mailto protocol - opens user's default email client
  const sendEmail = (subject, body) => {
    const mailtoLink = `mailto:john.asfar@worldwidecorporation.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    
    // Try multiple methods to open the email client
    let emailOpened = false;
    
    // Method 1: Try window.location.href
    try {
      window.location.href = mailtoLink;
      emailOpened = true;
    } catch (error) {
      // If that fails, try Method 2
    }
    
    // Method 2: Create and click a hidden anchor element (more reliable)
    if (!emailOpened) {
      try {
        const link = document.createElement('a');
        link.href = mailtoLink;
        link.target = '_blank';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        emailOpened = true;
      } catch (error) {
        // If that fails, try Method 3
      }
    }
    
    // Method 3: Fallback using window.open
    if (!emailOpened) {
      try {
        const newWindow = window.open(mailtoLink, '_blank');
        if (newWindow && !newWindow.closed) {
          emailOpened = true;
        }
      } catch (error) {
        // All methods failed
      }
    }
    
    // Show confirmation message
    if (emailOpened) {
      setSnackbarMessage('📧 Email client opened! Please review and send the email.');
      setSnackbarSeverity('success');
    } else {
      setSnackbarMessage('⚠️ Unable to open email client. Please manually send an email to john.asfar@worldwidecorporation.com with your request.');
      setSnackbarSeverity('warning');
    }
    setSnackbarOpen(true);
    
    // Close the dialog after a short delay
    setTimeout(() => {
      setBuyDialogOpen(false);
      setActiveStep(0);
      setPaymentProcessing(false);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        country: '',
        postalCode: '',
        message: '',
        agreeToTerms: false,
        quantity: 1,
        cardNumber: '',
        cardExpiry: '',
        cardCVC: '',
        cardName: '',
        billingAddress: '',
        billingCity: '',
        billingState: '',
        billingZip: '',
        billingCountry: ''
      });
    }, 3000);
    
    return emailOpened;
  };

  const sendPaymentRequestEmail = () => {
    setPaymentProcessing(true);
    
    const totalInfo = calculateTotalWithFee(selectedCoin?.priceNum || 0, parseInt(formData.quantity) || 1);
    
    const subject = `PAYPAL PAYMENT REQUEST: ${selectedCoin?.name} - ${selectedCoin?.price}`;
    const body = `
PAYPAL PAYMENT REQUEST

============================================
IMPORTANT: PayPal payment options are coming soon.
This is a payment request/inquiry.
============================================

Coin Information:
- Name: ${selectedCoin?.name}
- Year: ${selectedCoin?.year || '2024'}
- Grade: ${selectedCoin?.grade}
- Price: ${selectedCoin?.price}
- Metal: ${selectedCoin?.metal}
- Mint: ${selectedCoin?.mint || 'Not specified'}
- Special Offer: ${selectedCoin?.specialOffer ? 'YES - $99.00' : 'No'}
- PayPal Eligible: ${selectedCoin?.paypalEligible ? 'YES' : 'NO'}
- Face Value: ${selectedCoin?.faceValue || 'N/A'}
- Free Shipping: ${selectedCoin?.freeShipping ? 'YES' : 'NO'}

Order Details:
- Quantity: ${formData.quantity || 1}
- Subtotal: ${selectedCoin?.price}
- 2.5% Processing Fee: $${totalInfo.fee.toFixed(2)}
- Total: $${totalInfo.total.toFixed(2)}

Buyer Information:
- Full Name: ${formData.fullName}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Shipping Address: ${formData.address}
- City: ${formData.city}
- Country: ${formData.country}
- Postal Code: ${formData.postalCode}

Additional Message:
${formData.message || 'None'}

============================================
Please contact the buyer to arrange payment.
PayPal payment integration is coming soon.
============================================
    `;

    // Send email by opening default email client
    sendEmail(subject, body);
    
    setPaymentProcessing(false);
  };

  const handleSubmitBuyRequest = async () => {
    const isPayPalEligible = selectedCoin?.paypalEligible;
    
    if (isPayPalEligible) {
      sendPaymentRequestEmail();
      return;
    }
    
    // Email inquiry for gold coins
    const subject = `Purchase Inquiry: ${selectedCoin?.name}`;
    const body = `
PURCHASE INQUIRY

Coin Information:
- Name: ${selectedCoin?.name}
- Year: ${selectedCoin?.year || '2024'}
- Grade: ${selectedCoin?.grade}
- Price: ${selectedCoin?.price}
- Metal: ${selectedCoin?.metal}
- Mint: ${selectedCoin?.mint || 'Not specified'}
- Face Value: ${selectedCoin?.faceValue || 'N/A'}

Buyer Information:
- Full Name: ${formData.fullName}
- Email: ${formData.email}
- Phone: ${formData.phone}
- Address: ${formData.address}
- City: ${formData.city}
- Country: ${formData.country}
- Postal Code: ${formData.postalCode}

Additional Message:
${formData.message}
    `;

    // Send email by opening default email client
    sendEmail(subject, body);
  };

  const handleNext = () => {
    if (activeStep === 1) {
      if (!formData.fullName || !formData.email || !formData.phone || !formData.address || !formData.city || !formData.country) {
        setSnackbarMessage('Please fill in all required fields');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
      if (!formData.email.includes('@')) {
        setSnackbarMessage('Please enter a valid email address');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    if (key === 'priceRange') {
      return value[0] > 0 || value[1] < 100000;
    }
    if (key === 'searchQuery') {
      return value !== '';
    }
    return value !== '' && value !== 'All';
  }).length;

  const renderSpecialOfferSection = () => {
    return (
      <Box sx={{ width: '100%' }}>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#D32F2F',
            color: '#FFFFFF',
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 2,
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              pointerEvents: 'none',
            }
          }}
        >
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              mb: 1
            }}
          >
            SPECIAL OFFER $99
          </Typography>
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
              mb: 2
            }}
          >
            SILVER EAGLE
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              maxWidth: '700px',
              mx: 'auto',
              opacity: 0.95,
              fontSize: '1.1rem'
            }}
          >
            Newly Minted Silver Eagle • $99.00 + 2.5% fee • Free Shipping • Request Payment with PayPal
          </Typography>
        </Paper>

        <Grid container spacing={4} justifyContent="center">
          {allSpecialCoins.map((coin, index) => (
            <Grid item xs={12} sm={8} md={6} lg={4} key={coin.id}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ width: '100%' }}
              >
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  {coin.specialOffer && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: '#D32F2F',
                        color: '#FFFFFF',
                        px: 2,
                        py: 0.5,
                        borderRadius: 20,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        boxShadow: '0 2px 8px rgba(211,47,47,0.4)',
                      }}
                    >
                      Special Offer
                    </Box>
                  )}

                  {coin.paypalEligible && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 56,
                        right: 16,
                        zIndex: 10,
                        backgroundColor: '#0070BA',
                        color: '#FFFFFF',
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 16,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        boxShadow: '0 2px 8px rgba(0,112,186,0.3)',
                      }}
                    >
                      <PaymentIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                      PayPal
                    </Box>
                  )}

                  {coin.freeShipping && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 80,
                        left: 16,
                        zIndex: 10,
                        backgroundColor: '#4CAF50',
                        color: '#FFFFFF',
                        px: 1.5,
                        py: 0.4,
                        borderRadius: 16,
                        fontSize: '0.6rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        boxShadow: '0 2px 8px rgba(76,175,80,0.3)',
                      }}
                    >
                      Free Shipping
                    </Box>
                  )}

                  <CardMedia
                    component="img"
                    image={coin.image}
                    alt={coin.name}
                    sx={{
                      height: 280,
                      objectFit: 'contain',
                      p: 3,
                      backgroundColor: '#F5F5F5',
                    }}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/280x280/C6A75E/FFFFFF?text=' + encodeURIComponent(coin.type || 'Coin');
                    }}
                  />

                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        color: '#2D2D2D',
                        mb: 1,
                        fontSize: '1rem',
                        minHeight: 50,
                      }}
                    >
                      {coin.name}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={coin.year} 
                        size="small" 
                        sx={{ backgroundColor: alpha('#C6A75E', 0.1), color: 'primary.main' }} 
                      />
                      <Chip 
                        label={coin.grade} 
                        size="small" 
                        sx={{ backgroundColor: alpha('#4CAF50', 0.1), color: '#4CAF50' }} 
                      />
                      {coin.faceValue && (
                        <Chip 
                          label={`Face: ${coin.faceValue}`} 
                          size="small" 
                          sx={{ backgroundColor: alpha('#FFD700', 0.1), color: '#FFD700' }} 
                        />
                      )}
                    </Box>

                    <Typography 
                      variant="body2" 
                      sx={{ color: '#616161', mb: 2, fontSize: '0.85rem', minHeight: 40 }}
                    >
                      {coin.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography variant="body2" sx={{ color: '#9E9E9E', fontWeight: 500, fontSize: '0.7rem', textTransform: 'uppercase' }}>
                          Price
                        </Typography>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            color: coin.specialOffer ? '#D32F2F' : 'primary.main',
                            fontWeight: 800,
                            fontSize: '1.5rem',
                          }}
                        >
                          {coin.price}
                        </Typography>
                        {coin.priceNum > 0 && (
                          <Typography variant="caption" sx={{ color: '#616161', fontWeight: 500, display: 'block' }}>
                            +2.5% fee = ${(coin.priceNum * 1.025).toFixed(2)}
                          </Typography>
                        )}
                        {coin.freeShipping && (
                          <Typography variant="caption" sx={{ color: '#4CAF50', fontWeight: 600, display: 'block' }}>
                            <VerifiedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }} />
                            Free Shipping
                          </Typography>
                        )}
                      </Box>
                      {coin.paypalEligible ? (
                        <Button
                          variant="contained"
                          startIcon={<PaymentIcon />}
                          onClick={() => handlePayPalPurchase(coin)}
                          sx={{
                            backgroundColor: '#0070BA',
                            '&:hover': {
                              backgroundColor: '#005A94',
                            },
                            py: 1.5,
                            px: 3,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '0.8rem',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Request Payment
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          startIcon={<EmailIcon />}
                          onClick={() => handleBuyClick(coin)}
                          sx={{
                            backgroundColor: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                            py: 1.5,
                            px: 3,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '0.8rem',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Email Inquiry
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mt: 8 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              mb: 4,
              fontWeight: 700,
              color: '#2D2D2D',
              textAlign: 'center'
            }}
          >
            Silver & Gold Eagles - History & Specs
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              mb: 4,
              textAlign: 'center',
              color: '#616161',
              maxWidth: '700px',
              mx: 'auto'
            }}
          >
            Learn about the rich history and technical specifications of these iconic coins
          </Typography>

          <Grid container spacing={3} justifyContent="center">
            {eagleHistory.map((item, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#C6A75E', 0.15),
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Chip 
                        label={item.year} 
                        size="small" 
                        sx={{ backgroundColor: alpha('#C6A75E', 0.1), color: 'primary.main', mb: 2 }} 
                      />
                      <Typography variant="body2" sx={{ color: '#616161', lineHeight: 1.7 }}>
                        {item.description}
                      </Typography>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="#9E9E9E">Weight</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.specs.weight}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="#9E9E9E">Purity</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.specs.purity}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="#9E9E9E">Diameter</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.specs.diameter}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="#9E9E9E">Thickness</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D2D2D' }}>
                            {item.specs.thickness}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ width: { xs: '100%', sm: '150px' }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box
                        component="img"
                        src={item.image}
                        alt={item.title}
                        sx={{
                          width: '100%',
                          maxWidth: '150px',
                          height: 'auto',
                          objectFit: 'contain',
                          borderRadius: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          backgroundColor: '#F5F5F5',
                          p: 1,
                        }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/150x150/C6A75E/FFFFFF?text=Coin';
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Paper
          sx={{
            mt: 6,
            p: 3,
            backgroundColor: alpha('#C6A75E', 0.05),
            border: '1px solid',
            borderColor: alpha('#C6A75E', 0.2),
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2D2D2D', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOfferIcon sx={{ color: 'primary.main' }} />
            Important Information
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <VerifiedIcon sx={{ color: '#4CAF50' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Newly Minted Coin" 
                secondary="American Silver Eagle - freshly minted issue" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <PaymentIcon sx={{ color: '#0070BA' }} />
              </ListItemIcon>
              <ListItemText 
                primary="PayPal Payment Options Coming Soon" 
                secondary="Request payment via email - PayPal integration in development" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <LocalOfferIcon sx={{ color: '#D32F2F' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Special Price: $99.00" 
                secondary="Limited time offer for 1oz Silver Eagle" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <AttachMoneyIcon sx={{ color: '#FFD700' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Gold Coins - Email Inquiry Only" 
                secondary="For Gold MapleLeafs, please email us directly for pricing" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <ShoppingCartIcon sx={{ color: '#4CAF50' }} />
              </ListItemIcon>
              <ListItemText 
                primary="Free Shipping" 
                secondary="Free shipping on all Silver Eagle orders" 
              />
            </ListItem>
          </List>
        </Paper>
      </Box>
    );
  };

  const FilterContent = () => (
    <Box sx={{ p: isMobile ? 3 : 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#2D2D2D' }}>
          <TuneIcon sx={{ color: 'primary.main' }} /> Filters
          {activeFilterCount > 0 && (
            <Chip 
              label={activeFilterCount} 
              size="small" 
              sx={{
                backgroundColor: 'primary.main',
                color: '#FFFFFF',
                fontWeight: 600,
              }}
            />
          )}
        </Typography>
        {isMobile && (
          <IconButton onClick={() => setMobileFilterOpen(false)} sx={{ color: '#616161' }}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      <TextField
        fullWidth
        placeholder="Search coins by name, description, or year..."
        variant="outlined"
        size="small"
        value={filters.searchQuery}
        onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'primary.main' }} />
            </InputAdornment>
          ),
        }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Era</InputLabel>
        <Select
          value={filters.era || 'All'}
          label="Era"
          onChange={(e) => handleFilterChange('era', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {eras.map(era => (
            <MenuItem key={era} value={era}>{era}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Metal</InputLabel>
        <Select
          value={filters.metal || 'All'}
          label="Metal"
          onChange={(e) => handleFilterChange('metal', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {metals.map(metal => (
            <MenuItem key={metal} value={metal}>{metal}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <Autocomplete
          options={countriesList}
          value={filters.country || null}
          onChange={(event, newValue) => {
            handleFilterChange('country', newValue || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <PublicIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
            },
          }}
        />
      </FormControl>

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel sx={{ color: '#616161' }}>Grade</InputLabel>
        <Select
          value={filters.grade || 'All'}
          label="Grade"
          onChange={(e) => handleFilterChange('grade', e.target.value === 'All' ? '' : e.target.value)}
          sx={{ backgroundColor: '#FFFFFF' }}
        >
          {grades.map(grade => (
            <MenuItem key={grade} value={grade}>{grade}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom sx={{ color: '#2D2D2D' }}>Price Range</Typography>
        <Slider
          value={filters.priceRange}
          onChange={(e, val) => handleFilterChange('priceRange', val)}
          valueLabelDisplay="auto"
          min={0}
          max={100000}
          step={1000}
          valueLabelFormat={(value) => `$${value.toLocaleString()}`}
          sx={{
            color: 'primary.main',
            '& .MuiSlider-thumb': {
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-track': {
              backgroundColor: 'primary.main',
            },
            '& .MuiSlider-rail': {
              backgroundColor: alpha('#9E9E9E', 0.3),
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
            ${filters.priceRange[0].toLocaleString()}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
            ${filters.priceRange[1].toLocaleString()}
          </Typography>
        </Box>
      </Box>

      <TextField
        fullWidth
        label="Year"
        placeholder="e.g., 1907"
        value={filters.year}
        onChange={(e) => handleFilterChange('year', e.target.value)}
        sx={{ mb: 3 }}
      />

      <Button 
        fullWidth 
        variant="outlined" 
        onClick={clearFilters}
        sx={{ 
          mt: 2,
          borderColor: alpha('#9E9E9E', 0.3),
          color: '#616161',
          '&:hover': {
            borderColor: 'primary.main',
            color: 'primary.main',
            backgroundColor: alpha('#C6A75E', 0.04),
          },
        }}
      >
        Clear All Filters
      </Button>
    </Box>
  );

  const buySteps = ['Select Coin', 'Your Details', 'Confirm Purchase'];

  const getStepContent = (step) => {
    const isPayPal = selectedCoin?.paypalEligible;
    const totalInfo = selectedCoin?.priceNum > 0 ? calculateTotalWithFee(selectedCoin.priceNum, parseInt(formData.quantity) || 1) : null;
    
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
              {selectedCoin?.name}
            </Typography>
            {selectedCoin?.specialOffer && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOfferIcon />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Special Offer: {selectedCoin.price} - PayPal Eligible
                  </Typography>
                </Box>
              </Alert>
            )}
            {selectedCoin?.freeShipping && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedIcon sx={{ color: '#4CAF50' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Free Shipping Included!
                  </Typography>
                </Box>
              </Alert>
            )}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Year</Typography>
                <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                  {selectedCoin?.year}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Grade</Typography>
                <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                  {selectedCoin?.grade}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="#9E9E9E">Price</Typography>
                <Typography variant="body2" sx={{ color: selectedCoin?.specialOffer ? '#D32F2F' : 'primary.main', fontWeight: 700 }}>
                  {selectedCoin?.price}
                </Typography>
              </Grid>
            </Grid>
            {selectedCoin?.faceValue && (
              <Typography variant="body2" sx={{ color: '#616161', mb: 2 }}>
                Face Value: {selectedCoin.faceValue}
              </Typography>
            )}
            {isPayPal ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaymentIcon />
                  <Typography variant="body2">
                    This coin is eligible for PayPal payment request. Complete the form below and we'll send you payment instructions. PayPal payment options are coming soon!
                  </Typography>
                </Box>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Please review the coin details before proceeding with your email inquiry.
              </Alert>
            )}
          </Box>
        );
      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="fullName"
              value={formData.fullName}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              name="phone"
              value={formData.phone}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Shipping Address"
              name="address"
              value={formData.address}
              onChange={handleFormChange}
              margin="normal"
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData.city}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleFormChange}
                  margin="normal"
                  required
                />
              </Grid>
            </Grid>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Autocomplete
                options={countriesList}
                value={formData.country}
                onChange={(event, newValue) => {
                  setFormData({ ...formData, country: newValue || '' });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Country"
                    required
                  />
                )}
              />
            </FormControl>
            
            {isPayPal && (
              <TextField
                fullWidth
                label="Quantity"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleFormChange}
                margin="normal"
                InputProps={{
                  inputProps: { min: 1, max: 100 }
                }}
              />
            )}
            
            {isPayPal && selectedCoin?.priceNum > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#2D2D2D' }}>
                  Payment Request Details
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon sx={{ color: '#ED6C02' }} />
                    <Typography variant="body2">
                      <strong>PayPal payment options are coming soon!</strong> We'll send you payment instructions via email.
                    </Typography>
                  </Box>
                </Alert>
                <Paper sx={{ p: 2, backgroundColor: alpha('#0070BA', 0.05), border: '1px solid', borderColor: alpha('#0070BA', 0.2) }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PaymentIcon sx={{ color: '#0070BA' }} />
                    <span>Payment request for <strong>{selectedCoin?.price}</strong> x {formData.quantity || 1}</span>
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#616161' }}>
                    + 2.5% processing fee = <strong>${totalInfo?.total.toFixed(2)}</strong>
                  </Typography>
                  {selectedCoin?.freeShipping && (
                    <Typography variant="caption" sx={{ display: 'block', color: '#4CAF50' }}>
                      <VerifiedIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> Free Shipping included
                    </Typography>
                  )}
                </Paper>
              </Box>
            )}
            
            <TextField
              fullWidth
              label="Additional Message (Optional)"
              name="message"
              value={formData.message}
              onChange={handleFormChange}
              margin="normal"
              multiline
              rows={3}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DescriptionIcon sx={{ color: 'primary.main' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            {paymentProcessing ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" color="#616161">
                  Opening your email client...
                </Typography>
              </Box>
            ) : (
              <>
                <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 60, mb: 2 }} />
                <Typography variant="h6" gutterBottom sx={{ color: '#2D2D2D' }}>
                  {isPayPal ? 'Ready to Request Payment' : 'Ready to Inquire?'}
                </Typography>
                <Typography variant="body2" color="#616161" sx={{ mb: 3 }}>
                  Click the button below to open your email client with all your details pre-filled.
                  <br />
                  <strong>Please review and send the email to complete your request.</strong>
                </Typography>
                <Paper sx={{ p: 2, backgroundColor: alpha('#C6A75E', 0.05), mb: 2, textAlign: 'left' }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ color: '#2D2D2D' }}>
                    Purchase Summary
                  </Typography>
                  <Typography variant="body2" color="#616161">
                    {selectedCoin?.name}
                  </Typography>
                  <Typography variant="body2" color="#616161">
                    Quantity: {formData.quantity || 1}
                  </Typography>
                  {selectedCoin?.faceValue && (
                    <Typography variant="body2" color="#616161">
                      Face Value: {selectedCoin.faceValue}
                    </Typography>
                  )}
                  <Typography variant="h6" sx={{ color: selectedCoin?.specialOffer ? '#D32F2F' : 'primary.main', fontWeight: 700 }}>
                    {selectedCoin?.price}
                  </Typography>
                  {isPayPal && selectedCoin?.priceNum > 0 && totalInfo && (
                    <Typography variant="body2" color="#616161">
                      + 2.5% fee = ${totalInfo.total.toFixed(2)}
                    </Typography>
                  )}
                  {selectedCoin?.freeShipping && (
                    <Typography variant="caption" sx={{ color: '#4CAF50', display: 'block', mt: 1 }}>
                      <VerifiedIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> Free Shipping
                    </Typography>
                  )}
                  {isPayPal && (
                    <Chip 
                      icon={<PaymentIcon />}
                      label="Payment Request"
                      size="small"
                      sx={{ 
                        backgroundColor: '#0070BA', 
                        color: '#FFFFFF',
                        mt: 1,
                        '& .MuiChip-icon': { color: '#FFFFFF' }
                      }}
                    />
                  )}
                </Paper>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption" color="#616161">
                    Your email client will open with the email pre-filled. Just click "Send" to complete your request.
                  </Typography>
                </Alert>
              </>
            )}
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 6 }}>
          <Typography variant="h2" gutterBottom sx={{ color: '#2D2D2D' }}>
            Acquire
            <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
              Rare Coins
            </Box>
          </Typography>
          <Typography variant="body1" color="#616161" sx={{ maxWidth: '600px' }}>
            Browse our curated selection of investment-grade coins from the United States, Canada, 
            Australia, New Zealand, and the United Kingdom. Each coin is authenticated and certified by 
            leading numismatic authorities.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              borderBottom: `1px solid ${alpha('#9E9E9E', 0.2)}`,
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                minWidth: 120,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocalOfferIcon sx={{ color: '#D32F2F' }} />
                  <Box component="span" sx={{ fontWeight: 700 }}>Special Offer</Box>
                  <Chip 
                    label="$99" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#D32F2F', 
                      color: '#FFFFFF',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      height: 20,
                      '& .MuiChip-label': { px: 1 }
                    }} 
                  />
                </Box>
              }
            />
            <Tab 
              label="All Coins" 
              icon={<PublicIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {tabValue === 0 ? (
          renderSpecialOfferSection()
        ) : (
          <Grid container spacing={4}>
            {!isMobile && (
              <Grid item md={3}>
                <Paper 
                  sx={{ 
                    p: 3,
                    backgroundColor: '#FFFFFF',
                    border: '1px solid',
                    borderColor: alpha('#9E9E9E', 0.15),
                    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                    position: 'sticky',
                    top: 100,
                  }}
                >
                  <FilterContent />
                </Paper>
              </Grid>
            )}

            {isMobile && (
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={() => setMobileFilterOpen(true)}
                  sx={{ 
                    py: 2,
                    borderColor: alpha('#9E9E9E', 0.3),
                    color: '#616161',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      backgroundColor: alpha('#C6A75E', 0.04),
                    },
                  }}
                >
                  Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
                </Button>
              </Grid>
            )}

            <Drawer
              anchor="bottom"
              open={mobileFilterOpen}
              onClose={() => setMobileFilterOpen(false)}
              PaperProps={{
                sx: {
                  backgroundColor: '#FFFFFF',
                  borderTop: '2px solid',
                  borderColor: 'primary.main',
                  maxHeight: '90vh',
                }
              }}
            >
              <FilterContent />
            </Drawer>

            <Grid item xs={12} md={9}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body1" color="#616161">
                  Showing {paginatedCoins.length > 0 ? ((page - 1) * itemsPerPage) + 1 : 0}-
                  {Math.min(page * itemsPerPage, filteredCoins.length)} of {filteredCoins.length} results
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select 
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    sx={{ 
                      backgroundColor: '#FFFFFF',
                      color: '#2D2D2D',
                    }}
                  >
                    <MenuItem value="newest">Newest First</MenuItem>
                    <MenuItem value="price-asc">Price: Low to High</MenuItem>
                    <MenuItem value="price-desc">Price: High to Low</MenuItem>
                    <MenuItem value="grade">Grade: High to Low</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              {paginatedCoins.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <Typography variant="h6" color="#616161">
                    No coins found matching your filters.
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={clearFilters}
                    sx={{ mt: 2 }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              ) : (
                <>
                  <Grid container spacing={4}>
                    {paginatedCoins.map((coin) => (
                      <Grid item xs={12} sm={6} lg={4} key={coin.id}>
                        <CoinCard 
                          coin={coin} 
                          onPriceInquiry={() => handleBuyClick(coin)}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                      <Pagination 
                        count={totalPages} 
                        page={page}
                        onChange={(e, val) => setPage(val)}
                        color="primary"
                        size={isMobile ? "medium" : "large"}
                        sx={{
                          '& .MuiPaginationItem-root': {
                            color: '#616161',
                            '&.Mui-selected': {
                              backgroundColor: 'primary.main',
                              color: '#FFFFFF',
                              '&:hover': {
                                backgroundColor: 'primary.dark',
                              },
                            },
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}

              <Paper
                sx={{
                  mt: 6,
                  p: 3,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid',
                  borderColor: alpha('#9E9E9E', 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <PublicIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>
                  <Typography variant="body2" sx={{ color: '#2D2D2D', fontWeight: 600 }}>
                    Global Collection
                  </Typography>
                  <Typography variant="caption" color="#616161">
                    Coins available from {countriesList.length} countries worldwide
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      <Dialog 
        open={buyDialogOpen} 
        onClose={() => {
          if (!paymentProcessing) {
            setBuyDialogOpen(false);
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Typography variant="h4" color="primary" gutterBottom>
            {selectedCoin?.paypalEligible ? 'Payment Request' : 'Purchase Inquiry'}
          </Typography>
          {!paymentProcessing && (
            <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
              {buySteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          )}
        </DialogTitle>

        <DialogContent>
          {getStepContent(activeStep)}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', p: 3 }}>
          {!paymentProcessing && (
            <>
              <Button
                onClick={() => setBuyDialogOpen(false)}
                variant="outlined"
              >
                Cancel
              </Button>
              <Box>
                {activeStep > 0 && (
                  <Button
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                )}
                {activeStep === buySteps.length - 1 ? (
                  <Button
                    onClick={handleSubmitBuyRequest}
                    variant="contained"
                    startIcon={selectedCoin?.paypalEligible ? <PaymentIcon /> : <EmailIcon />}
                    sx={{
                      backgroundColor: selectedCoin?.paypalEligible ? '#0070BA' : undefined,
                      '&:hover': {
                        backgroundColor: selectedCoin?.paypalEligible ? '#005A94' : undefined,
                      },
                    }}
                  >
                    {selectedCoin?.paypalEligible ? 'Request Payment' : 'Send Inquiry'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    variant="contained"
                  >
                    Next
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuyCoins;